import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useCopyOverride, safeColor, COPY_DEFAULTS } from "@/lib/copyOverrides";
import { getPopupImageUrlById } from "@/lib/popupImages";
import { parseAccommodationIds, amenitiesForAccommodations } from "@/lib/accommodationAmenities";
import { parsePopupCards } from "@/lib/popupCards";
import { iconForBullet } from "@/lib/popupBulletIcons";

interface Props { onCta?: () => void; interactive?: boolean; }

const NAVY = "#1f4e6b";
const WARM_RED = "#e0603f";

function parseImageUrls(raw: string): string[] {
  return (raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => /^https:\/\//i.test(s));
}

const OvernightPopupCard = ({ onCta, interactive = true }: Props) => {
  const headline = useCopyOverride("popup.headline", COPY_DEFAULTS["popup.headline"]);
  const price    = useCopyOverride("popup.price",    COPY_DEFAULTS["popup.price"]);
  const subline  = useCopyOverride("popup.subline",  COPY_DEFAULTS["popup.subline"]);
  const accsRaw  = useCopyOverride("popup.accommodations", COPY_DEFAULTS["popup.accommodations"]);
  const ctaLabel = useCopyOverride("popup.cta",      COPY_DEFAULTS["popup.cta"]);
  const imageId  = useCopyOverride("popup.image", COPY_DEFAULTS["popup.image"]);
  const imagesRaw = useCopyOverride("popup.images", COPY_DEFAULTS["popup.images"]);
  const accentRaw = useCopyOverride("popup.accent", COPY_DEFAULTS["popup.accent"]);
  const buttonFgRaw = useCopyOverride("popup.buttonFg", COPY_DEFAULTS["popup.buttonFg"]);
  const cardsRaw = useCopyOverride("popup.cards", COPY_DEFAULTS["popup.cards"]);

  const accent = safeColor(accentRaw);
  const buttonFg = safeColor(buttonFgRaw);

  const legacyImg = getPopupImageUrlById(imageId);
  const urls = useMemo(() => parseImageUrls(imagesRaw), [imagesRaw]);

  const [broken, setBroken] = useState<Record<string, true>>({});
  const usable = urls.filter((u) => !broken[u]);
  const slides = usable.length > 0 ? usable : [legacyImg];

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef<number | null>(null);

  useEffect(() => { setIndex(0); }, [imagesRaw]);

  useEffect(() => {
    if (slides.length < 2 || paused) return;
    const t = window.setInterval(() => setIndex((i) => (i + 1) % slides.length), 4000);
    return () => window.clearInterval(t);
  }, [slides.length, paused]);

  const current = slides[Math.min(index, slides.length - 1)];
  const next = slides.length > 1 ? slides[(index + 1) % slides.length] : null;

  const customCards = useMemo(() => parsePopupCards(cardsRaw), [cardsRaw]);
  const amenities = amenitiesForAccommodations(parseAccommodationIds(accsRaw));

  return (
    <div className="w-full overflow-hidden rounded-xl bg-card shadow-soft">
      <div
        className="relative w-full aspect-[16/9] bg-muted"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={(e) => { touchX.current = e.touches[0]?.clientX ?? null; }}
        onTouchEnd={(e) => {
          const start = touchX.current;
          touchX.current = null;
          if (start == null || slides.length < 2) return;
          const dx = (e.changedTouches[0]?.clientX ?? start) - start;
          if (Math.abs(dx) < 40) return;
          setIndex((i) => (dx < 0 ? (i + 1) % slides.length : (i - 1 + slides.length) % slides.length));
        }}
      >
        <img
          key={current}
          src={current}
          alt="Son's Island aerial view"
          className="w-full h-full object-cover"
          loading={index === 0 ? "eager" : "lazy"}
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => { if (usable.length > 0) setBroken((b) => ({ ...b, [current]: true })); }}
        />
        {next && (
          <img src={next} alt="" aria-hidden className="hidden" loading="eager" referrerPolicy="no-referrer" />
        )}
        {slides.length > 1 && (
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
            {slides.map((u, i) => (
              <button
                key={u}
                type="button"
                aria-label={`Show image ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-1.5 w-1.5 rounded-full transition-opacity ${i === index ? "bg-background opacity-100" : "bg-background opacity-50"}`}
              />
            ))}
          </div>
        )}
      </div>
      <div className="px-5 sm:px-7 pt-5 pb-6">
        <h2
          className="font-display text-[30px] font-bold leading-tight text-center whitespace-pre-wrap"
          style={{ color: NAVY }}
        >{headline}</h2>
        <p
          className="mt-2 text-[20px] font-bold text-center whitespace-pre-wrap"
          style={{ color: accent || WARM_RED }}
        >{price}</p>
        {subline && (
          <p className="mt-2 text-[22px] font-normal text-center text-[#3f4a52] whitespace-pre-wrap">{subline}</p>
        )}
        {customCards.length > 0 ? (
          <div className="mt-5 space-y-4">
            {customCards.map((c) => (
              <div key={c.id} className="rounded-xl border border-[#e3e6e8] bg-white p-5 text-left">
                <p
                  className="font-display text-[20px] font-semibold leading-snug text-center"
                  style={{ color: NAVY }}
                >{c.title}</p>
                {c.price && (
                  <p
                    className="mt-1 text-[15px] font-semibold text-center"
                    style={{ color: accent || WARM_RED }}
                  >{c.price}</p>
                )}
                {c.bullets.length > 0 && (
                  <ul className="mt-3.5 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2">
                    {c.bullets.map((b, i) => {
                      const Icon = iconForBullet(b);
                      return (
                        <li key={i} className="flex items-center gap-2 text-[15px] leading-snug text-[#3f4a52]">
                          <Icon className="h-[15px] w-[15px] shrink-0 text-[#2f7f9e]" strokeWidth={2} />
                          <span>{b}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
        ) : amenities.length > 0 ? (
          <div className="mt-5 rounded-xl border border-[#e3e6e8] bg-white p-5 text-left">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2">
              {amenities.map(({ id, label }) => (
                <li key={id} className="flex items-start gap-2 text-[15px] leading-snug text-[#3f4a52]">
                  <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#2f7f9e]" />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <Button
          onClick={interactive ? onCta : undefined}
          disabled={!interactive}
          style={{
            backgroundColor: accent || NAVY,
            color: buttonFg || "#ffffff",
          }}
          className="w-full mt-5 font-bold uppercase tracking-wide text-base py-6 rounded-xl shadow-soft disabled:opacity-100 hover:opacity-90"
        >{ctaLabel}</Button>
      </div>
    </div>
  );
};

export default OvernightPopupCard;
